import {makeObservable, observable, runInAction} from "mobx";
import {IResidentAdminService} from "../../../services/residentAdminService";
import {IUserService} from "../../../services/userService";
import ResidentAccountCreateDetails from "./models/residentAccountCreateDetails";
import {ResidentAccountDetail} from "./models/residentAccountDetail";
import ResidentDetail from "./models/residentDetail";
import {AppAccessStatus} from "../../../shared/enums/appAccessStatus";
import {SortOrder} from "../../../shared/enums/sortOrder";
import {IResidentActionRequiredCountsDomainStore} from "./residentActionRequiredCountsDomainStore";

/**
 * The interface for ResidentAdminDomainStore.
 */
export interface IResidentAdminDomainStore {
  /**
   * An array of users.
   */
  residents: ResidentDetail[];

  /**
   * A Parameterised resident search method.
   *
   * `departmentTypeCodes` scopes the search to one or more audiences (e.g. a
   * unified calendar covering both Independent and Serviced residents). When
   * omitted, the store's configured department is used.
   */
  searchResidents: (
    page?: number,
    limit?: number,
    sortField?: string,
    sortOrder?: SortOrder,
    searchTerm?: string,
    departmentTypeCodes?: string[]
  ) => Promise<number>;

  /**
   * Loads the residents from the backend and set the residentList property of ResidentsAdminDomainStore.
   * The method returns the total count of residents currently in the data store.
   */
  loadVillageResidents: () => Promise<number>;

  /**
   * Creates an account for a resident
   */
  createAccount: (
    resident: ResidentAccountCreateDetails
  ) => Promise<ResidentAccountDetail>;

  /**
   * Remove an account for a resident
   */
  removeAccount: (
    resident: ResidentAccountCreateDetails
  ) => Promise<ResidentAccountDetail>;

  /**
   * Change a resident's account email address.
   */
  changeEmail: (id: string, email: string) => Promise<any>;

  /**
   * Reset a resident's password (creates new temporary password in the backend).
   */
  resetPassword: (id: string) => Promise<ResidentAccountDetail>;

  /**
   * True if data is loading.
   */
  isLoading: boolean;

  /**
   * True if empty search result is coming form the backend.
   */
  hasNoSearchResult: boolean;

  /**
   * True once resident data has been loaded from the backend at least once.
   */
  hasLoadedResidentData: boolean;

  /**
   * Reset the residents list to it's default state
   */
  resetResidents: () => void;

  /**
   * Get the code of the selected village.
   */
  getVillageCode: () => string;
}

/**
 * The store that manage the logic and state of resident domain objects.
 */
export class ResidentAdminDomainStore implements IResidentAdminDomainStore {
  private readonly residentAdminService: IResidentAdminService;
  private readonly userService: IUserService;
  private readonly departmentTypeCode?: string;
  private readonly actionRequiredCountsStore?: IResidentActionRequiredCountsDomainStore;
  private pendingResidentsQuery?: Promise<number>;
  private pendingResidentsQueryKey?: string;
  private queuedResidentsQuery?: {
    key: string;
    promise: Promise<number>;
  };
  private requestVersion: number = 0;
  residents: ResidentDetail[] = [];
  isLoading: boolean = false;
  hasNoSearchResult: boolean = false;
  hasLoadedResidentData: boolean = false;
  /**
   * Instantiates a new ResidentsAdminDomainStore.
   */
  constructor(
    residentAdminService: IResidentAdminService,
    userService: IUserService,
    departmentTypeCode?: string,
    actionRequiredCountsStore?: IResidentActionRequiredCountsDomainStore
  ) {
    makeObservable(this, {
      residents: observable,
      isLoading: observable,
      hasNoSearchResult: observable,
      hasLoadedResidentData: observable
    });
    this.residentAdminService = residentAdminService;
    this.userService = userService;
    this.departmentTypeCode = departmentTypeCode;
    this.actionRequiredCountsStore = actionRequiredCountsStore;
  }

  searchResidents = async (
    page?: number,
    limit?: number,
    sortField?: string,
    sortOrder?: SortOrder,
    searchTerm?: string,
    departmentTypeCodes?: string[]
  ) => {
    const villageId = this.userService.getSelectedVillage()?.id;
    if (!villageId) {
      return 0;
    }
    // Explicit codes (resolved from the selected calendar's audience) win;
    // otherwise fall back to this store's configured department.
    const resolvedCodes: (string | undefined)[] =
      departmentTypeCodes && departmentTypeCodes.length > 0
        ? departmentTypeCodes
        : [this.departmentTypeCode];
    const queryKey = JSON.stringify({
      type: "searchResidents",
      villageId,
      page,
      limit,
      sortField,
      sortOrder,
      searchTerm,
      departmentTypeCodes: [...resolvedCodes].sort()
    });

    return this.runResidentsQuery(queryKey, () =>
      this.searchResidentsForDepartments(
        resolvedCodes,
        villageId,
        page,
        limit,
        sortField,
        sortOrder,
        searchTerm
      )
    );
  };

  // Issues one search per department code and merges the results, de-duping by
  // platformUserId and re-sorting by name: the concatenated responses would
  // otherwise arrive grouped by department rather than integrated.
  private searchResidentsForDepartments = async (
    departmentTypeCodes: (string | undefined)[],
    villageId: string,
    page?: number,
    limit?: number,
    sortField?: string,
    sortOrder?: SortOrder,
    searchTerm?: string
  ) => {
    if (departmentTypeCodes.length <= 1) {
      return this.residentAdminService.searchResidents(
        villageId,
        page,
        limit,
        sortField,
        sortOrder,
        searchTerm,
        departmentTypeCodes[0]
      );
    }

    const responses = await Promise.all(
      departmentTypeCodes.map((code) =>
        this.residentAdminService.searchResidents(
          villageId,
          page,
          limit,
          sortField,
          sortOrder,
          searchTerm,
          code
        )
      )
    );

    const residentsById = new Map<string, any>();
    for (const response of responses) {
      for (const resident of response?.residents ?? []) {
        residentsById.set(resident.platformUserId, resident);
      }
    }

    // The booking picker never passes a sort field, so the backend returns
    // these merged results unordered; sort by name here to present a stable,
    // integrated list rather than one grouped by department.
    const byName = (a: any, b: any) =>
      (a.lastName ?? "").localeCompare(b.lastName ?? "", undefined, {
        sensitivity: "base"
      }) ||
      (a.firstName ?? "").localeCompare(b.firstName ?? "", undefined, {
        sensitivity: "base"
      });

    const residents = Array.from(residentsById.values()).sort(byName);
    return {residents, totalCount: residents.length};
  };

  private runResidentsQuery = async (
    queryKey: string,
    query: () => Promise<any>
  ): Promise<number> => {
    if (this.pendingResidentsQuery) {
      // The newest call represents the latest intent. When it matches the
      // in-flight query, any queued distinct-key query is now stale and must
      // be dropped so it does not run afterwards and overwrite `residents`
      // with older intent (e.g. "a" -> "ab" -> "a" must end on "a" results).
      if (this.pendingResidentsQueryKey === queryKey) {
        this.queuedResidentsQuery = undefined;
        return this.pendingResidentsQuery;
      }

      if (this.queuedResidentsQuery?.key === queryKey) {
        return this.queuedResidentsQuery.promise;
      }

      const queuedPromise = this.pendingResidentsQuery
        .catch(() => undefined)
        .then(() => {
          if (this.queuedResidentsQuery?.key !== queryKey) {
            // Superseded by a newer queued query; resolve with its result.
            return this.queuedResidentsQuery?.promise ?? this.residents.length;
          }

          this.queuedResidentsQuery = undefined;
          return this.runResidentsQuery(queryKey, query);
        });

      this.queuedResidentsQuery = {
        key: queryKey,
        promise: queuedPromise
      };

      return queuedPromise;
    }

    const queryVersion = this.requestVersion;
    runInAction(() => {
      this.isLoading = true;
      this.hasNoSearchResult = false;
    });

    const residentsQuery = query()
      .then((data) => {
        runInAction(() => {
          if (queryVersion === this.requestVersion) {
            this.residents = this.mapResidents(data.residents);
            this.hasNoSearchResult = this.residents.length === 0;
            this.hasLoadedResidentData = true;
          }
          // A queued successor is about to run; don't drop the loading state.
          if (!this.queuedResidentsQuery) {
            this.isLoading = false;
          }
        });
        return data.totalCount;
      })
      .catch((error) => {
        runInAction(() => {
          if (!this.queuedResidentsQuery) {
            this.isLoading = false;
          }
        });
        throw error;
      })
      .finally(() => {
        if (this.pendingResidentsQuery === residentsQuery) {
          this.pendingResidentsQuery = undefined;
          this.pendingResidentsQueryKey = undefined;
        }
      });

    this.pendingResidentsQuery = residentsQuery;
    this.pendingResidentsQueryKey = queryKey;

    return residentsQuery;
  };

  loadVillageResidents = async () => {
    const villageId = this.userService.getSelectedVillage()?.id;
    if (!villageId) {
      return 0;
    }
    const queryKey = JSON.stringify({
      type: "loadVillageResidents",
      villageId,
      departmentTypeCode: this.departmentTypeCode
    });

    return this.runResidentsQuery(queryKey, () => {
      // Matches runResidentsQuery's version check so a discarded list doesn't update the badge.
      const version = this.requestVersion;
      return this.residentAdminService
        .loadVillageResidents(villageId, this.departmentTypeCode)
        .then((data) => {
          if (
            version === this.requestVersion &&
            this.actionRequiredCountsStore &&
            this.departmentTypeCode
          ) {
            this.actionRequiredCountsStore.setDepartmentCount(
              villageId,
              this.departmentTypeCode,
              (data?.residents ?? []).filter(
                (r: any) =>
                  AppAccessStatus[
                    r.appAccessStatus as keyof typeof AppAccessStatus
                  ] === AppAccessStatus.RequiresAction
              ).length
            );
          }
          return data;
        });
    });
  };

  private mapResidents = (residents: any[]) =>
    residents.map(
      (d: any) =>
        new ResidentDetail(
          d.platformUserId,
          d.firstName,
          d.lastName,
          d.preferredName,
          d.unitCode,
          d.phoneNumber,
          d.mobileNumber,
          d.emailAddress,
          d.appAccountEmail,
          d.accountStatus,
          d.townhouseBuddy,
          undefined,
          AppAccessStatus[d.appAccessStatus as keyof typeof AppAccessStatus],
          d.personTypeId
        )
    );

  getVillageCode = (): string => {
    return this.userService.getSelectedVillage()?.code || "";
  };

  createAccount = async (
    resident: ResidentAccountCreateDetails
  ): Promise<ResidentAccountDetail> => {
    return await this.residentAdminService.createAccount(resident);
  };

  removeAccount = async (
    resident: ResidentAccountCreateDetails
  ): Promise<ResidentAccountDetail> => {
    return await this.residentAdminService.removeAccount(resident);
  };

  changeEmail = async (
    id: string,
    email: string
  ): Promise<ResidentAccountDetail> => {
    return await this.residentAdminService.changeEmail(id, email);
  };

  resetResidents = () => {
    runInAction(() => {
      this.requestVersion += 1;
      // Detach the key so new calls queue behind the invalidated in-flight query instead of coalescing with it.
      this.pendingResidentsQueryKey = undefined;
      this.queuedResidentsQuery = undefined;
      this.residents = [];
      this.isLoading = false;
      this.hasNoSearchResult = false;
      this.hasLoadedResidentData = false;
    });
  };

  resetPassword = async (id: string): Promise<ResidentAccountDetail> => {
    return await this.residentAdminService.resetPassword(id);
  };
}
